/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import {
  collection,
  doc,
  getDocs,
  increment,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { toast } from 'react-hot-toast';

import { useEffect, useState } from 'react';

import bcryptjs from 'bcryptjs';
import dayjs from 'dayjs';
import {
  ArrowRight,
  Box,
  CheckCircle2,
  Clock,
  Copy,
  Database,
  Filter,
  Pickaxe,
  Search,
} from 'lucide-react';
import { db } from '../config/firebaseConfig';
import { UserAuth } from '../context/authContext';
import FirestoreTimestampToDate from './utils/FirebaseTimetoDate';
import { calculateHash } from './utils/helper';

const CopyButton = ({ text, className = '' }) => (
  <button
    onClick={(e) => {
      e.preventDefault();
      navigator.clipboard.writeText(text);
      toast.success('Copied successfully');
    }}
    className={`p-1 hover:bg-white/10 rounded transition-colors group ${className}`}
    title="Copy to clipboard"
  >
    <Copy
      size={12}
      className="text-white/40 group-hover:text-white/80 transition-colors"
    />
  </button>
);

// Constants for AstraPOS
const TARGET_BLOCKS = 5;

export default function BlocksTable({ embedded = false }) {
  const [roomId, setId] = useState(localStorage.getItem('roomId') ?? '');
  const [selectedBlock, setSelectedBlock] = useState();

  const [blocks, setBlocks] = useState([]);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('hash');
  const { user } = UserAuth();

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const fetchRoomTransactions = async () => {
    if (!roomId) return;

    try {
      const roomRef = doc(db, 'rooms', roomId);

      let tempBlocks = [];

      const blocksRes = await getDocs(
        query(collection(roomRef, 'blocks'), orderBy('timestamp', 'desc'))
      );

      tempBlocks = [
        ...tempBlocks,
        ...blocksRes.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })),
      ];

      setBlocks(tempBlocks);

      const transactionsRes = await getDocs(
        query(
          collection(roomRef, 'transactions'),
          where('status', '==', 'Added to block'),
          orderBy('addedAt', 'asc')
        )
      );

      const transactions = transactionsRes.docs.reduce(
        (result, value, index, array) => {
          if (index % 5 === 0)
            result.push({
              transactions: array
                .slice(index, index + 5)
                .map((v) => ({ id: v.id, ...v.data() })),
            });
          return result;
        },
        []
      );

      setPendingTransactions(transactions);
    } catch (error) {
      console.error('Error fetching room transactions:', error);
      toast.error('Error fetching room transactions.');
    }
  };

  async function saveBlock(
    calculatedHash,
    timestamp,
    previousBlockHash,
    transactions,
    nonce
  ) {
    try {
      // Get room data to check if it's AstraPOS
      const roomSnapshot = await getDocs(
        query(collection(db, 'rooms'), where('__name__', '==', roomId))
      );

      let roomData = null;
      if (roomSnapshot.docs.length > 0) {
        roomData = roomSnapshot.docs[0].data();
      } else {
        // Fallback: get room directly
        const roomRef = doc(db, 'rooms', roomId);
        const snap = await getDocs(query(collection(db, 'rooms')));
        const foundRoom = snap.docs.find((d) => d.id === roomId);
        if (foundRoom) {
          roomData = foundRoom.data();
        }
      }

      const isAstraPos = roomData?.roomType === 'ASTRAPOS';
      const gameCycle = isAstraPos ? roomData?.gameState?.currentCycle : null;

      // Transaction approval rewards (1% of each transaction)
      const txApprovalReward = transactions.reduce((acc, curr) => {
        return acc + curr.amount * 0.01;
      }, 0);

      // Fixed block reward
      const BLOCK_REWARD = 50;
      const totalMinerReward = txApprovalReward + BLOCK_REWARD;
      const receivers = Object.fromEntries(
        (
          await getDocs(
            query(
              collection(db, 'users'),
              where(
                'publicKey',
                'in',
                transactions.map((t) => t.receiver)
              )
            )
          )
        ).docs.map((userData) => [
          userData.data().publicKey,
          { id: userData.id, ...userData.data() },
        ])
      );

      // Get current miner's public key from Firestore
      const minerUserDoc = await getDocs(
        query(collection(db, 'users'), where('email', '==', user.email))
      );
      const minerPublicKey =
        minerUserDoc.docs.length > 0
          ? minerUserDoc.docs[0].data().publicKey
          : null;

      // Create system reward transaction only if we have miner's public key
      const systemRewardTx = minerPublicKey
        ? {
            sender: 'AstraMiner/AstraPOS/AstraHack',
            senderEmail: 'system',
            receiver: minerPublicKey,
            amount: totalMinerReward,
            status: 'Added to chain',
            verified: true,
            blockStatus: 'added',
            signature: 'SYSTEM_REWARD',
            nonce: 0,
            hash: bcryptjs.hashSync(
              `BLOCK_REWARD_${calculatedHash}_${gameCycle || 'classic'}`
            ),
            timestamp: new Date(),
          }
        : null;

      // Include system reward transaction in the block only if it was created
      const allBlockTransactions = systemRewardTx
        ? [...transactions, systemRewardTx]
        : transactions;

      await runTransaction(db, async (dbTransaction) => {
        // Save block with gameCycle field for AstraPOS
        const blockData = {
          timestamp,
          previousBlockHash,
          hash: calculatedHash,
          transactions: allBlockTransactions,
          nonce,
          created_at: serverTimestamp(),
        };

        if (isAstraPos) {
          blockData.gameCycle = gameCycle;
        }

        dbTransaction.set(
          doc(db, 'rooms', roomId, 'blocks', calculatedHash),
          blockData
        );

        // Save system reward transaction as a document in transactions collection
        if (systemRewardTx) {
          const rewardTxRef = doc(
            collection(db, 'rooms', roomId, 'transactions')
          );
          dbTransaction.set(rewardTxRef, systemRewardTx);
        }

        // Reward for miner (transaction approval fee + block reward)
        dbTransaction.update(doc(db, 'users', user.email), {
          balance: increment(totalMinerReward),
        });

        transactions.forEach((t) => {
          // Send transaction amount (receiver gets 99%, miner gets 1% as approval fee)
          receivers.hasOwnProperty(t.receiver) &&
            dbTransaction.update(
              doc(db, 'users', receivers[t.receiver].email),
              {
                balance: increment(t.amount * 0.99),
              }
            );

          dbTransaction.update(doc(db, 'rooms', roomId, 'transactions', t.id), {
            status: 'Added to chain',
            gameCycle: gameCycle, // For AstraPOS tracking
          });
        });

        // For AstraPOS: increment block count and check if target reached
        if (isAstraPos && roomData?.gameState?.stage === 'PLAYING') {
          const newBlockCount =
            (roomData.gameState?.blocksMinedThisCycle || 0) + 1;

          dbTransaction.update(doc(db, 'rooms', roomId), {
            'gameState.blocksMinedThisCycle': newBlockCount,
          });

          // After transaction commits, check if we should transition to leaderboard
          // This will be handled by real-time listener in GameTab
        }
      });

      toast.success('Added block to blockchain');

      // Only redirect if not embedded (single-page mode)
      if (!embedded) {
        // For AstraPOS, redirect to game instead of joinedroom
        if (isAstraPos) {
          window.location.href = '/game';
        } else {
          window.location.href = '/joinedroom';
        }
      }

      fetchRoomTransactions();
    } catch (e) {
      console.error('Failed to save block', e);
      toast.error('Failed to save block');
    }
  }

  useEffect(() => {
    if (roomId) fetchRoomTransactions();
  }, [roomId]);

  return (
    <div className={embedded ? 'p-0' : 'min-h-screen p-6 bg-[#0B0E14]'}>
      <div className={embedded ? '' : 'max-w-7xl mx-auto space-y-8'}>
        {/* Header */}
        {!embedded && (
          <div className="flex flex-col gap-2">
            <h2 className="text-3xl font-bold text-white flex items-center gap-3">
              <Database className="text-[#6C4FFF]" size={32} />
              Blockchain Explorer
            </h2>
            <p className="text-white/40">
              Visualizing blocks, transactions, and validating.
              room{' '}
              <span className="text-[#6C4FFF] font-mono ml-1">{roomId}</span>
            </p>
          </div>
        )}

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 group-focus-within:text-[#6C4FFF] transition-colors"
              size={18}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search blocks by hash, nonce, or status..."
              className="w-full bg-[#1E2330] border border-white/10 rounded-xl pl-12 pr-4 py-3.5 text-white text-sm outline-none focus:border-[#6C4FFF] focus:ring-1 focus:ring-[#6C4FFF] transition-all placeholder:text-white/30"
            />
          </div>
          <div className="relative min-w-[200px]">
            <Filter
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"
              size={16}
            />
            <select
              value={filter}
              onChange={handleFilterChange}
              className="w-full appearance-none bg-[#1E2330] border border-white/10 rounded-xl pl-11 pr-10 py-3.5 text-white text-sm outline-none focus:border-[#6C4FFF] transition-all cursor-pointer hover:border-white/20"
            >
              <option value="hash">Transaction Hash</option>
              <option value="nonce">Nonce</option>
              <option value="sender">Sender</option>
              <option value="receiver">Receiver</option>
              <option value="signature">Signature</option>
              <option value="status">Status</option>
              <option value="timestamp">Timestamp</option>
              <option value="amount">Amount</option>
            </select>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none border-l border-white/10 pl-3">
              <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-white/40"></div>
            </div>
          </div>
        </div>

        {/* Block Chain Diagram with Arrows */}
        <div className="space-y-4">
          <h3 className="text-white font-bold text-lg flex items-center gap-2">
            <Box size={20} className="text-[#6C4FFF]" /> Chain Visualization
          </h3>
          <div className="overflow-x-auto pb-6 scrollbar-thin scrollbar-thumb-[#6C4FFF]/20 scrollbar-track-transparent">
            <BlockChainDiagram blocks={blocks} />
          </div>
        </div>

        {/* Blocks Table */}
        <div className="bg-[#1E2330]/50 backdrop-blur rounded-2xl border border-white/5 overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5 bg-white/[0.02]">
                  <th className="text-left py-5 px-6 text-white/40 text-xs font-bold uppercase tracking-wider">
                    Block Info
                  </th>
                  <th className="text-left py-5 px-6 text-white/40 text-xs font-bold uppercase tracking-wider">
                    Transactions
                  </th>
                  <th className="text-left py-5 px-6 text-white/40 text-xs font-bold uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {blocks &&
                  [...pendingTransactions, ...blocks].map((block, index) => (
                    <tr
                      key={index}
                      className="hover:bg-white/[0.02] transition-colors"
                    >
                      {!block.hash ? (
                        <>
                          <td className="py-6 px-6 align-top">
                            <div className="flex flex-col gap-1">
                              {block.hash ? (
                                <span className="text-white font-mono text-sm break-all">
                                  {block.hash}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-full text-xs font-medium w-fit">
                                  <Clock size={12} /> Pending Validation
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-6 px-6 align-top">
                            {block.status === 'genesis' ? (
                              <span className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-full text-xs font-medium">
                                <CheckCircle2 size={12} /> Genesis Block
                              </span>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="w-full min-w-[500px] border-separate border-spacing-y-2">
                                  <thead>
                                    <tr>
                                      <th className="text-left text-white/30 text-[10px] font-bold uppercase px-2">
                                        Tx Hash
                                      </th>
                                      <th className="text-left text-white/30 text-[10px] font-bold uppercase px-2">
                                        Time
                                      </th>
                                      <th className="text-left text-white/30 text-[10px] font-bold uppercase px-2">
                                        Sender
                                      </th>
                                      <th className="text-left text-white/30 text-[10px] font-bold uppercase px-2">
                                        Receiver
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {block.transactions &&
                                      block.transactions.length > 0 &&
                                      block.transactions.map((t, idx) => (
                                        <tr
                                          key={idx}
                                          className="bg-white/[0.02] rounded-lg"
                                        >
                                          <td className="py-2 px-2">
                                            <div className="flex items-center gap-2">
                                              <span className="text-white/80 text-xs font-mono">
                                                {t.hash?.substring(0, 8)}...
                                              </span>
                                              <CopyButton text={t.hash} />
                                            </div>
                                          </td>
                                          <td className="py-2 px-2 text-white/60 text-xs">
                                            {t.addedAt &&
                                              FirestoreTimestampToDate(
                                                t.addedAt
                                              )}
                                          </td>
                                          <td className="py-2 px-2 text-white/60 text-xs font-mono">
                                            {t.sender?.substring(0, 6)}...
                                          </td>
                                          <td className="py-2 px-2 text-white/60 text-xs font-mono">
                                            {t.receiver?.substring(0, 6)}...
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                          <td className="py-6 px-6 align-top">
                            {block.status !== 'genesis' && !block.id && (
                              <button
                                className="inline-flex items-center gap-2 px-4 py-2 bg-[#6C4FFF] hover:bg-[#5840D9] text-white rounded-xl transition-all shadow-lg shadow-[#6C4FFF]/20 text-sm font-semibold"
                                onClick={() => {
                                  setSelectedBlock({
                                    transactions:
                                      (block.transactions &&
                                        block.transactions) ||
                                      [],
                                    previousBlockHash: blocks[0]?.hash,
                                  });
                                  toast.success(
                                    'Block selected for validation'
                                  );
                                }}
                              >
                                <Pickaxe size={16} />
                                Validate Block
                              </button>
                            )}
                          </td>
                        </>
                      ) : (
                        ''
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        {/* eslint-disable-next-line react/jsx-no-comment-textnodes */}
        </div>
// Validation Visualizer
        <div className="bg-[#1E2330]/80 backdrop-blur-md rounded-2xl p-8 border border-white/10 shadow-2xl">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2 flex items-center justify-center gap-3">
              <Pickaxe className="text-[#6C4FFF]" size={28} />
              Validation Station
            </h2>
            <p className="text-white/40 max-w-lg mx-auto">
              Visualizing the proof-of-stake mechanism. Find a nonce that
              results in a hash starting with the required leading zeros.
            </p>
          </div>

          <Visualizer
            transactions={(selectedBlock && selectedBlock.transactions) || []}
            previousBlockHash={
              (selectedBlock && selectedBlock.previousBlockHash) || undefined
            }
            save={saveBlock}
          />
        </div>
      </div>
    </div>
  );
}

function Visualizer({
  transactions = [],
  previousBlockHash,
  initialNonce = 0,
  timestamp = dayjs().unix(),
  difficulty = 2,
  save,
}) {
  const [nonce, setNonce] = useState(initialNonce ?? 0);
  const [calculatedHash, setCalculatedHash] = useState('');
  const [foundHash, setFoundHash] = useState(false);
  const [roomId, setRoomId] = useState(localStorage.getItem('roomId') ?? '');
  const [isSaving, setIsSaving] = useState(false);

  async function mine(
    hash = calculateHash(transactions, nonce, timestamp, previousBlockHash)
  ) {
    if (foundHash) return;

    const start = Date.now();
    let nonce = initialNonce;

    while (hash.slice(0, difficulty) !== '0'.repeat(difficulty) && !foundHash) {
      const current = Date.now();
      hash = calculateHash(transactions, nonce, timestamp, previousBlockHash);
      setCalculatedHash(hash);
      const currentTime = Math.floor((current - start) / 1000);
      setNonce(++nonce);
      await new Promise((res) => setTimeout(res, 5));
    }

    setFoundHash(true);
    const end = Date.now();
    const time = (end - start) / 1000;
  }

  return (
    <div className="max-w-3xl mx-auto">
      {transactions && transactions.length < 1 && (
        <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-white/5 rounded-xl bg-white/[0.01]">
          <Box size={48} className="text-white/10 mb-4" />
          <p className="text-white/40 font-medium">
            Select a block from the table above to start validating
          </p>
        </div>
      )}
      {transactions && transactions.length > 0 && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Nonce */}
            <div className="bg-black/20 rounded-xl p-4 border border-white/5">
              <h4 className="text-white/60 text-xs font-bold uppercase mb-2">
                Current Nonce
              </h4>
              <div className="font-mono text-2xl text-white tracking-widest">
                {nonce}
              </div>
            </div>
            {/* Found Hash */}
            <div
              className={`rounded-xl p-4 border transition-colors ${foundHash ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-black/20 border-white/5'}`}
            >
              <h4
                className={`${foundHash ? 'text-emerald-400' : 'text-white/60'} text-xs font-bold uppercase mb-2`}
              >
                Status
              </h4>
              <div
                className={`font-mono text-xl ${foundHash ? 'text-emerald-400' : 'text-white'} font-semibold`}
              >
                {foundHash ? 'HASH FOUND!' : 'SEARCHING...'}
              </div>
            </div>
          </div>

          {/* Calculated Hash */}
          <div className="bg-black/30 rounded-xl p-5 border border-white/10">
            <h4 className="text-white/60 text-xs font-bold uppercase mb-3">
              Calculated Hash Output
            </h4>
            <div className="font-mono text-sm break-all text-[#6C4FFF] leading-relaxed">
              {calculatedHash || 'Waiting to start...'}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-4 justify-center pt-2">
            <button
              className="px-8 py-3 bg-gradient-to-r from-[#6C4FFF] to-[#5840D9] text-white rounded-xl hover:shadow-[0_0_20px_rgba(108,79,255,0.4)] transition-all font-semibold flex items-center gap-2"
              onClick={() => mine()}
            >
              <Pickaxe size={18} />
              Start Validation
            </button>
            {foundHash && (
              <button
                className="px-8 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-[0_0_20px_rgba(16,185,129,0.3)] transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={isSaving}
                onClick={async () => {
                  setIsSaving(true);
                  try {
                    await save(
                      calculatedHash,
                      timestamp,
                      previousBlockHash,
                      transactions,
                      nonce
                    );
                    toast.success('Saved to BlockChain');
                  } catch (error) {
                    console.error('Error saving block:', error);
                  } finally {
                    setIsSaving(false);
                  }
                }}
              >
                <CheckCircle2 size={18} />
                {isSaving ? 'Saving Block...' : 'Commit to Chain'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// BlockChain Diagram Component with Arrow Connectors
function BlockChainDiagram({ blocks = [] }) {
  return (
    <div className="w-full py-4">
      <div className="flex overflow-x-auto gap-8 pb-4 px-2 items-start">
        {blocks && blocks.length > 0 ? (
          blocks.map((b, index) => (
            <div
              key={b.id || index}
              className="flex gap-4 items-center shrink-0"
            >
              {/* Block Card */}
              <div className="bg-[#1E2330] rounded-2xl p-0 border border-white/10 w-[340px] shadow-xl overflow-hidden hover:border-[#6C4FFF]/40 transition-colors group">
                <div className="bg-white/[0.03] p-4 border-b border-white/5 flex justify-between items-center">
                  <h5 className="text-white font-bold text-lg flex items-center gap-2">
                    <Box size={16} className="text-[#6C4FFF]" />
                    Block #{blocks.length - 1 - index}
                  </h5>
                  <span className="text-xs text-white/40 font-mono">
                    {new Date(b.timestamp * 1000).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>

                <div className="p-5 space-y-4">
                  {/* Hash */}
                  <div>
                    <label className="text-white/40 text-[10px] font-bold uppercase mb-1 block">
                      Hash
                    </label>
                    <div className="font-mono text-xs text-white/80 bg-black/20 p-2 rounded truncate border border-white/5">
                      {b.hash || 'N/A'}
                    </div>
                  </div>

                  {/* Prev Hash */}
                  <div>
                    <label className="text-white/40 text-[10px] font-bold uppercase mb-1 block">
                      Prev Hash
                    </label>
                    <div className="font-mono text-xs text-white/60 bg-black/20 p-2 rounded truncate border border-white/5">
                      {b.previousBlockHash || 'Genesis'}
                    </div>
                  </div>

                  {/* Nonce & Diff */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-white/40 text-[10px] font-bold uppercase mb-1 block">
                        Nonce
                      </label>
                      <div className="text-white font-mono text-sm">
                        {b.nonce ?? 0}
                      </div>
                    </div>
                    <div>
                      <label className="text-white/40 text-[10px] font-bold uppercase mb-1 block">
                        Difficulty
                      </label>
                      <div className="text-white font-mono text-sm">0000</div>
                    </div>
                  </div>

                  <div className="h-px bg-white/5 my-2"></div>

                  {/* Transactions */}
                  <div>
                    <span className="text-white/80 text-xs font-semibold block mb-2">
                      {b.transactions?.length || 0} Transactions
                    </span>
                    <div className="h-32 overflow-y-auto space-y-1.5 pr-1 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                      {b.transactions && b.transactions.length > 0 ? (
                        b.transactions.map((t, txIndex) => (
                          <div
                            key={t.id || txIndex}
                            className="flex items-center gap-2 bg-white/5 rounded px-2 py-1.5"
                          >
                            <span className="text-white/40 text-[10px] font-mono min-w-[16px]">
                              {txIndex + 1}
                            </span>
                            <span className="text-white/70 text-[10px] font-mono truncate">
                              {t.hash}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-white/30 text-xs italic">
                          Empty block
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Arrow Connector */}
              {index < blocks.length - 1 && (
                <ArrowRight size={32} className="text-white/20" />
              )}
            </div>
          ))
        ) : (
          <div className="w-full text-center py-12">
            <p className="text-white/40 font-medium flex flex-col items-center gap-2">
              <Box size={40} className="opacity-20" />
              No blocks mined yet
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
